/**
 * TeamRolesStep — Step 3 of setup wizard.
 *
 * Shows required actor roles for the selected product's industry.
 * For ALF (assisted living), roles come from MN 144G qualification requirements.
 * User configures team size per role.
 *
 * Role data will migrate to backend constellation config; hardcoded here
 * until `roleDefinitions` query is available.
 */

import { useMemo } from 'react';
import {
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  Alert,
  ThemeIcon,
  NumberInput,
  SimpleGrid,
  Divider,
  Tooltip,
} from '@mantine/core';
import {
  IconUsers,
  IconShieldCheck,
  IconArrowRight,
  IconInfoCircle,
  IconCrown,
  IconStethoscope,
  IconHeartbeat,
  IconPill,
  IconUser,
  IconUserCheck,
} from '@tabler/icons-react';
import type { ProductDefinition } from './ProductSelectionStep';

export interface TeamRoleConfig {
  roleId: string;
  roleName: string;
  count: number;
  required: boolean;
  minCount: number;
  qualifications: string;
  statute?: string;
  delegationFrom?: string;
  delegationTo?: string[];
  icon: string;
}

export interface TeamRolesStepProps {
  product: ProductDefinition;
  teamRoles: TeamRoleConfig[];
  onTeamRolesChange: (roles: TeamRoleConfig[]) => void;
}

/**
 * Role definitions by product industry. Sourced from:
 * - ALF: MN 144G.45, 144G.50, 144G.71 qualification requirements
 * - Home Care: MN 245D licensing requirements
 * - Rehab: CMS CoP requirements
 *
 * TODO: Migrate to backend role config + GraphQL query.
 */
const INDUSTRY_ROLES: Record<string, TeamRoleConfig[]> = {
  assisted_living: [
    {
      roleId: 'ald',
      roleName: 'Assisted Living Director (ALD)',
      count: 1,
      required: true,
      minCount: 1,
      qualifications: 'Licensed administrator, 30-day training, background study',
      statute: 'MN 144G.50',
      delegationTo: ['rn'],
      icon: 'crown',
    },
    {
      roleId: 'rn',
      roleName: 'Registered Nurse (RN)',
      count: 1,
      required: true,
      minCount: 1,
      qualifications: 'Nursing assessment, medication delegation authority, clinical supervision',
      statute: 'MN 144G.45, 144G.71',
      delegationFrom: 'ald',
      delegationTo: ['lpn', 'med_aide', 'uap'],
      icon: 'stethoscope',
    },
    {
      roleId: 'lpn',
      roleName: 'Licensed Practical Nurse (LPN)',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: 'Medication administration, wound care, vital signs',
      statute: 'MN 144G.45',
      delegationFrom: 'rn',
      icon: 'heartbeat',
    },
    {
      roleId: 'med_aide',
      roleName: 'Medication Aide',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: 'Medication administration (with RN delegation), ADL documentation',
      statute: 'MN 144G.71 (delegated)',
      delegationFrom: 'rn',
      icon: 'pill',
    },
    {
      roleId: 'uap',
      roleName: 'Unlicensed Assistive Personnel (UAP)',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: 'ADL documentation, vital signs, EVV check-in/out',
      statute: 'MN 144G',
      delegationFrom: 'rn',
      icon: 'user',
    },
    {
      roleId: 'prescriber',
      roleName: 'Prescriber (External)',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: 'External provider — medication orders via EHR/pharmacy',
      icon: 'user_check',
    },
  ],
  home_care: [
    {
      roleId: 'rn_supervisor',
      roleName: 'RN Supervisor',
      count: 1,
      required: true,
      minCount: 1,
      qualifications: 'Licensed RN, supervisory experience, home care certification',
      statute: 'MN 245D',
      delegationTo: ['hha', 'pca'],
      icon: 'stethoscope',
    },
    {
      roleId: 'hha',
      roleName: 'Home Health Aide (HHA)',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: '75-hour training, competency evaluation, background check',
      statute: 'MN 245D',
      delegationFrom: 'rn_supervisor',
      icon: 'heartbeat',
    },
    {
      roleId: 'pca',
      roleName: 'Personal Care Assistant (PCA)',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: 'PCA training, background study',
      statute: 'MN 256B.0659',
      delegationFrom: 'rn_supervisor',
      icon: 'user',
    },
  ],
  outpatient_rehab: [
    {
      roleId: 'medical_director',
      roleName: 'Medical Director',
      count: 1,
      required: true,
      minCount: 1,
      qualifications: 'Licensed physician, rehab specialty',
      statute: '42 CFR Part 485',
      icon: 'crown',
    },
    {
      roleId: 'pt',
      roleName: 'Physical Therapist (PT)',
      count: 1,
      required: true,
      minCount: 1,
      qualifications: 'DPT or MPT degree, state license',
      icon: 'heartbeat',
    },
    {
      roleId: 'ot',
      roleName: 'Occupational Therapist (OT)',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: 'MOT or DOT degree, state license',
      icon: 'user_check',
    },
    {
      roleId: 'slp',
      roleName: 'Speech-Language Pathologist (SLP)',
      count: 0,
      required: false,
      minCount: 0,
      qualifications: 'Master\'s in SLP, CCC-SLP, state license',
      icon: 'user',
    },
  ],
};

function getRoleIcon(icon: string) {
  switch (icon) {
    case 'crown': return <IconCrown size={18} />;
    case 'stethoscope': return <IconStethoscope size={18} />;
    case 'heartbeat': return <IconHeartbeat size={18} />;
    case 'pill': return <IconPill size={18} />;
    case 'user_check': return <IconUserCheck size={18} />;
    default: return <IconUser size={18} />;
  }
}

export function TeamRolesStep({
  product,
  teamRoles,
  onTeamRolesChange,
}: TeamRolesStepProps) {
  const industryRoles = INDUSTRY_ROLES[product.industry] ?? [];

  // Initialize roles from industry template if teamRoles is empty
  const roles: TeamRoleConfig[] = useMemo(() => {
    if (teamRoles.length > 0) return teamRoles;
    return industryRoles.map((r) => ({ ...r }));
  }, [teamRoles, industryRoles]);

  // Ensure roles are initialized on first render
  if (teamRoles.length === 0 && industryRoles.length > 0) {
    onTeamRolesChange(industryRoles.map((r) => ({ ...r })));
  }

  const updateRoleCount = (roleId: string, count: number) => {
    const updated = roles.map((r) =>
      r.roleId === roleId ? { ...r, count: Math.max(r.minCount, count) } : r
    );
    onTeamRolesChange(updated);
  };

  const totalStaff = roles.reduce((sum, r) => sum + r.count, 0);
  const requiredRoles = roles.filter((r) => r.required);
  const optionalRoles = roles.filter((r) => !r.required);

  // Build delegation chain visualization
  const delegationChains = useMemo(() => {
    const chains: string[][] = [];
    const roots = roles.filter((r) => !r.delegationFrom && r.delegationTo?.length);

    for (const root of roots) {
      const chain = [root.roleName];
      const addChildren = (parentId: string) => {
        const children = roles.filter((r) => r.delegationFrom === parentId && r.count > 0);
        for (const child of children) {
          chain.push(child.roleName);
          if (child.delegationTo?.length) {
            addChildren(child.roleId);
          }
        }
      };
      if (root.delegationTo) addChildren(root.roleId);
      if (chain.length > 1) chains.push(chain);
    }

    return chains;
  }, [roles]);

  return (
    <Stack gap="md">
      <Group gap="xs">
        <ThemeIcon variant="light" size="lg" color="violet">
          <IconUsers size={20} />
        </ThemeIcon>
        <div>
          <Text fw={600} size="lg">Team & Roles</Text>
          <Text size="sm" c="dimmed">
            Configure the roles and team size for your {product.industry.replace(/_/g, ' ')} operation.
          </Text>
        </div>
      </Group>

      {/* Summary */}
      <Group gap="xs">
        <Badge size="md" variant="light" color="violet">
          {totalStaff} staff configured
        </Badge>
        <Badge size="sm" variant="outline" color="red">
          {requiredRoles.length} required role{requiredRoles.length !== 1 ? 's' : ''}
        </Badge>
        <Badge size="sm" variant="outline" color="gray">
          {optionalRoles.length} optional
        </Badge>
      </Group>

      {industryRoles.length === 0 ? (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          No role templates available for this product industry. You can configure roles after setup.
        </Alert>
      ) : (
        <>
          {/* Required Roles */}
          {requiredRoles.length > 0 && (
            <>
              <Text size="sm" fw={600} c="red.7">
                Required Roles
              </Text>
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                {requiredRoles.map((role) => (
                  <RoleCard
                    key={role.roleId}
                    role={role}
                    onCountChange={(count) => updateRoleCount(role.roleId, count)}
                  />
                ))}
              </SimpleGrid>
            </>
          )}

          {/* Optional Roles */}
          {optionalRoles.length > 0 && (
            <>
              <Divider label="Optional Roles" labelPosition="center" />
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                {optionalRoles.map((role) => (
                  <RoleCard
                    key={role.roleId}
                    role={role}
                    onCountChange={(count) => updateRoleCount(role.roleId, count)}
                  />
                ))}
              </SimpleGrid>
            </>
          )}

          {/* Delegation Chains */}
          {delegationChains.length > 0 && (
            <>
              <Divider />
              <Text size="sm" fw={600}>Delegation Chains</Text>
              <Paper p="sm" withBorder bg="gray.0" radius="md">
                <Stack gap="xs">
                  {delegationChains.map((chain, i) => (
                    <Group key={i} gap="xs" wrap="nowrap">
                      {chain.map((name, j) => (
                        <Group key={j} gap={4} wrap="nowrap">
                          {j > 0 && <IconArrowRight size={12} color="var(--mantine-color-gray-5)" />}
                          <Badge size="xs" variant="light" color={j === 0 ? 'violet' : 'gray'}>
                            {name}
                          </Badge>
                        </Group>
                      ))}
                    </Group>
                  ))}
                  <Text size="xs" c="dimmed">
                    Delegation authority flows from supervisors to direct care staff.
                  </Text>
                </Stack>
              </Paper>
            </>
          )}
        </>
      )}
    </Stack>
  );
}

function RoleCard({
  role,
  onCountChange,
}: {
  role: TeamRoleConfig;
  onCountChange: (count: number) => void;
}) {
  return (
    <Paper
      p="sm"
      withBorder
      style={{
        borderLeftWidth: 3,
        borderLeftColor: role.required
          ? 'var(--mantine-color-red-5)'
          : 'var(--mantine-color-gray-3)',
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon
              size="sm"
              variant="light"
              color={role.required ? 'violet' : 'gray'}
            >
              {getRoleIcon(role.icon)}
            </ThemeIcon>
            <Text size="sm" fw={600} truncate>
              {role.roleName}
            </Text>
          </Group>
          {role.required && (
            <Badge size="xs" color="red" variant="light">
              Required
            </Badge>
          )}
        </Group>

        <Text size="xs" c="dimmed">
          {role.qualifications}
        </Text>

        {role.statute && (
          <Group gap="xs">
            <IconShieldCheck size={12} color="var(--mantine-color-gray-5)" />
            <Text size="xs" c="dimmed" fs="italic">
              {role.statute}
            </Text>
          </Group>
        )}

        <NumberInput
          label="Team size"
          value={role.count}
          onChange={(val) => onCountChange(typeof val === 'number' ? val : 0)}
          min={role.minCount}
          max={99}
          size="xs"
          w={100}
        />
      </Stack>
    </Paper>
  );
}
